/**
 * VRG-714 — the pure half of the website funnel Sankey card: the coverage line, the chart's
 * nodes and bands, the small-sample rule and the notes. NO IMPORTS on purpose, like
 * `websiteAnalyticsModel.ts`, so `test/website-funnel-sankey.guard.spec.ts` can load it under
 * node's own runner. The shapes below are structural copies of the API's
 * (`services/websiteAnalyticsApi.ts` WebsiteFunnelResponse) and of `SankeyChart`'s props.
 */

/** The shared chart greys a node's or band's share below this many people (never hides it). */
export const SMALL_SAMPLE_BELOW = 10

export const FUNNEL_CHART_HEIGHT = 380

/** Glossary entries behind the coverage line's two tooltips and the card's (§44/§59). */
export const FUNNEL_TERMS = {
  allVisits: 'website-all-visits',
  analyticsOnPaths: 'website-visitor-paths',
  card: 'website-funnel-step-rate',
} as const

type NodeKind = 'start' | 'stage' | 'left' | 'joined'

export interface FunnelNodeIn {
  id: string
  label: string
  column: number
  kind: NodeKind
  n: number
}

export interface FunnelLinkIn {
  source: string
  target: string
  value: number
}

export interface FunnelResponseIn {
  posthog: { status: string; message?: string }
  vera: { status: string; message?: string }
  population: { visitors: number; visitorDays: number } | null
  nodes: readonly FunnelNodeIn[]
  links: readonly FunnelLinkIn[]
  waitlist: { total: number; inPopulation: number; outsidePopulation: number } | null
  inferred: { engaged: number; lead: number; people: number }
}

/** The tones `SankeyChart` knows: a kind maps to exactly one. */
export const TONE_FOR_KIND: Record<NodeKind, 'source' | 'stage' | 'end' | 'success'> = {
  start: 'source',
  stage: 'stage',
  left: 'end',
  joined: 'success',
}

export interface ChartNode {
  id: string
  label: string
  tone: 'source' | 'stage' | 'end' | 'success'
  n: number
  hint?: string
}

export interface ChartLink {
  source: string
  target: string
  value: number
  n: number
}

/** Where a "Left" node's people stopped, for its hover line (recharts draws every end on the right). */
const LEFT_HINT: Record<string, string> = {
  left_visit: 'Visited, then did neither: no Get started, no ROI calculator',
  left_engaged: 'Opened the form or started the calculator, then left before a lead',
  left_lead: 'Became a lead in this range, but did not join the waitlist in it',
}

/**
 * The chart's input, in the order the API gives (the card passes `preserveOrder`, so each
 * column keeps it and every "Left" is drawn last). `n` is the people behind each node and band:
 * the flow is weighted by people already, so it equals the value, and it is what the shared
 * chart's small-sample rule reads.
 */
export const funnelChartData = (r: FunnelResponseIn): { nodes: ChartNode[]; links: ChartLink[] } => ({
  nodes: r.nodes.map((node) => ({
    id: node.id,
    label: node.label,
    tone: TONE_FOR_KIND[node.kind],
    n: node.n,
    ...(LEFT_HINT[node.id] ? { hint: LEFT_HINT[node.id] } : {}),
  })),
  links: r.links.filter((l) => l.value > 0).map((l) => ({ source: l.source, target: l.target, value: l.value, n: l.value })),
})

const fmt = (n: number): string => n.toLocaleString('en-US')

const pct = (ratio: number): string => `${Math.round(ratio * 100)}%`

export type CoverageGap = 'no_vercel' | 'no_posthog' | 'today_window' | 'exceeds_total' | 'no_visits'

export interface Coverage {
  /** "101 visits in total (Vercel)", or what is missing. */
  totalText: string
  /** "72 visitors with analytics on, shown below". */
  shownText: string
  /** "≈79% of visits", or null when the two are not comparable here (see `gap`). */
  shareText: string | null
  share: number | null
  gap: CoverageGap | null
  /**
   * The computed caveat for the analytics-on tooltip: how the share was worked out, or why
   * there is none. The definitions themselves come from the glossary (`FUNNEL_TERMS`).
   */
  caveat: string
}

export interface CoverageInput {
  /** Vercel's visitors for the range (overview.allVisitors.visitors). */
  vercelVisits: number | null
  /** The chart's people and person-days (funnel.population). */
  population: { visitors: number; visitorDays: number } | null
  /** "Today" from the viewer's midnight: Vercel cannot cut there, PostHog can. */
  todayCut: boolean
}

/**
 * THE UNITS, which is what makes the percentage honest or not.
 *
 *   Vercel "visitors": identified by a hash of the request that is discarded after 24 hours
 *     (Vercel's privacy docs), so one person returning on three days is three. That is a VISIT
 *     count in all but name, and the line calls it visits.
 *   PostHog population: distinct PEOPLE (a cookie that lasts), which is what the chart counts.
 *
 * People ÷ visits would compare two different units and understate coverage on any range
 * longer than a day. So the share is person-days ÷ Vercel visits, both "one person on one
 * day", and is shown as approximate: Vercel's hash also changes with network and device,
 * and an ad blocker can hide a visitor from one tool and not the other.
 *
 * No share at all when the two cannot be compared: Today (different windows), a Vercel or
 * PostHog figure missing, or PostHog's person-days exceeding Vercel's visits (proof the two
 * are not counting the same visits, which a "≈ 104%" would hide).
 */
export const coverageLine = ({ vercelVisits, population, todayCut }: CoverageInput): Coverage => {
  const totalText = vercelVisits === null ? 'Vercel visits unavailable' : `${fmt(vercelVisits)} visits in total (Vercel)`
  const shownText =
    population === null ? 'visitors with analytics on unavailable' : `${fmt(population.visitors)} visitors with analytics on, shown below`
  const gap: CoverageGap | null =
    vercelVisits === null
      ? 'no_vercel'
      : population === null
        ? 'no_posthog'
        : vercelVisits === 0
          ? 'no_visits'
          : todayCut
            ? 'today_window'
            : population.visitorDays > vercelVisits
              ? 'exceeds_total'
              : null
  const share = gap === null && population && vercelVisits ? population.visitorDays / vercelVisits : null
  let caveat: string
  switch (gap) {
    case null:
      caveat = `≈${pct(share as number)} compares visits with visits: these ${fmt(population!.visitors)} people made ${fmt(
        population!.visitorDays,
      )} daily visits, of the ${fmt(vercelVisits!)} Vercel counted. Vercel counts without cookies and PostHog only people who allowed analytics, and the two identify people differently, so it is approximate.`
      break
    case 'today_window':
      caveat =
        'No share for Today: Vercel counts the whole UTC day, PostHog counts from your local midnight, so the two cover different windows.'
      break
    case 'exceeds_total':
      caveat = `No share: PostHog counted more daily visits (${fmt(population!.visitorDays)}) than Vercel's visits (${fmt(
        vercelVisits!,
      )}), so in this range the two are not counting the same visits.`
      break
    case 'no_visits':
      caveat = 'No share: Vercel counted no visits in this range.'
      break
    default:
      caveat = 'No share: one of the two counts is unavailable.'
  }
  return { totalText, shownText, shareText: share === null ? null : `≈${pct(share)} of visits`, share, gap, caveat }
}

/**
 * The note under the chart about waitlist joins the chart cannot show: people who joined in the
 * range with no analytics-on visit in it. A note, never a band, so the flow still adds up.
 */
export const waitlistNote = (r: Pick<FunnelResponseIn, 'waitlist' | 'vera'>): string | null => {
  if (!r.waitlist) return r.vera.status === 'error' ? 'Waitlist joins could not be read, so the chart has no Joined waitlist band.' : null
  const { outsidePopulation: out, total } = r.waitlist
  if (out <= 0) return null
  return `${fmt(out)} of ${fmt(total)} waitlist ${total === 1 ? 'join' : 'joins'} in this range came from ${
    out === 1 ? 'someone' : 'people'
  } with no analytics-on visit, so ${out === 1 ? 'it is' : 'they are'} not in the chart.`
}

/** People placed at a stage by a later step: said, never hidden. */
export const inferredNote = (r: Pick<FunnelResponseIn, 'inferred'>): string | null => {
  const n = r.inferred.people
  if (n <= 0) return null
  return `${fmt(n)} ${n === 1 ? 'person is' : 'people are'} placed by a later step (for example, a form submitted with no "opened" event, because analytics was turned on mid-form).`
}

/** The empty state, plainly: 0 visitors with analytics on means there are no paths to draw. */
export const EMPTY_POPULATION_TEXT = 'No visitors with analytics on in this range, so there are no paths to show.'
